import { useChat } from "@ai-sdk/react";
import {
  JSONParseError,
  lastAssistantMessageIsCompleteWithToolCalls,
  TypeValidationError,
} from "ai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { displayToast } from "../../components/app/AppToast";
import { refetchApiQuery } from "../../services/api";
import { useEffectOnce } from "../app/hooks/useEffectOnce";
import { useFirstTokenHaptic } from "../gpt/hooks/useFirstTokenHaptic";
import {
  assistantUIMessages,
  uiMessageHasAnswer,
  uiMessageRequest,
  type GeneralChatUIMessage,
} from "../gpt/hooks/useGptChat";
import type { AssistantDataParts } from "../../../../api/src/shared/assistant-ui-message";
import {
  createAssistantChatTransport,
  type AssistantOrigin,
} from "./assistantChatTransport";
import {
  withServerSummary,
  type Redaction,
  type RedactionNotice,
} from "./redaction";
import { usePatientDataInputRuleSeen } from "../gpt/stores/patientDataInputRuleStore";
import {
  gaEventGptAnswerWaitEnded,
  gaEventGptPatientDataRedacted,
} from "../../tools/analytics/domains/gpt.analytics";

// What a retry replays. Every request is idempotent on its ids.
type Attempt =
  | { kind: "send"; origin: AssistantOrigin }
  | { kind: "regenerate"; runId: string; requestId: string }
  | { kind: "answer" }
  | { kind: "resume" };

// A stream that breaks with no HTTP response and no `error` chunk lost its connection, not its run:
// it reopens after each pause, and the failure shows once they are spent.
const RECONNECT_DELAYS_MILLISECONDS = [1000, 3000, 10_000] as const;
const httpFailureSchema = z.object({ responseStatus: z.number() });

// An HTTP answer or a malformed event would come back the same on each reconnect.
function lostConnection(error: Error): boolean {
  return (
    !httpFailureSchema.safeParse(error).success &&
    !JSONParseError.isInstance(error) &&
    !TypeValidationError.isInstance(error)
  );
}

// The reader's wait for the run of their last input: the message, or the card answer (REQ-12).
type AnswerWait = {
  startedAt: number;
  preAnswer: "shown" | "none";
  // Null until the reader sees the pre-answer or the answer draft.
  firstWordsAt: number | null;
  // Null until the published text ends with its sources; the stream then waits up to a minute for the follow-up question.
  answeredAt: number | null;
};

// `onData` hands over its part's data untyped.
const preAnswerDataSchema = z.object({
  status: z.enum(["writing", "written", "failed"]),
  text: z.string().nullable(),
}) satisfies z.ZodType<AssistantDataParts["preAnswer"]>;

function secondsSince(startedAt: number, at: number): number {
  return Math.round((at - startedAt) / 100) / 10;
}

// The history leaves an active run's message to the stream, so its question comes last.
// A question copied from a legacy Search turn comes last too, with no answer and no run.
function activeRunId(messages: GeneralChatUIMessage[]): string | null {
  const last = messages.at(-1);
  if (last === undefined || last.role !== "user") return null;
  if (last.metadata === undefined)
    throw new Error("should not happen: a question without its metadata");
  return last.metadata.runId;
}

// `useChat` on the assistant engine, with the commands its UI sends besides messages.
export function useAssistantChat(input: {
  conversationId: string;
  initialMessages: GeneralChatUIMessage[];
  // False while a question awaits its first send, which opens its own stream.
  // True resyncs the history at mount, then resumes its active run.
  resume: boolean;
  onTurnAccepted: () => void;
  // The question moved to a new conversation, with the Notice the reader sees.
  onTurnMoved: (conversationId: string, notice: RedactionNotice | null) => void;
  onTurnFinish: () => void;
  // The server could not check the last send for patient data and stored nothing (AD-20).
  // Captures that send at once, and returns the call that shows it as unsent.
  onRedactionRefused: () => () => void;
  // The Notice of a send that moved here from another conversation.
  initialNotice: RedactionNotice | null;
}) {
  const { t } = useTranslation();
  const conversationId = input.conversationId;
  // Latest-value ref: the transport lives as long as the conversation.
  const onTurnAccepted = useRef(input.onTurnAccepted);
  onTurnAccepted.current = input.onTurnAccepted;
  const onTurnMoved = useRef(input.onTurnMoved);
  onTurnMoved.current = input.onTurnMoved;
  const onRedactionRefused = useRef(input.onRedactionRefused);
  onRedactionRefused.current = input.onRedactionRefused;
  // The Notice of the last send, live only: a reload shows the Markers without it (AD-18).
  const [notice, setNotice] = useState<RedactionNotice | null>(
    input.initialNotice
  );
  // Latest-value ref: the banner follows the reader to the new conversation.
  const shownNotice = useRef(notice);
  shownNotice.current = notice;
  // Set once `chat` exists: the transport is created before it.
  const showServerText = useRef<(text: string) => void>(() => {
    throw new Error("should not happen: a send before the chat exists");
  });
  // Null on a run the page resumed, whose input it did not see: the history replay measures no wait.
  const wait = useRef<AnswerWait | null>(null);
  function startWait() {
    wait.current = {
      startedAt: performance.now(),
      preAnswer: "none",
      firstWordsAt: null,
      answeredAt: null,
    };
  }
  const assistant = useMemo(
    () =>
      createAssistantChatTransport({
        conversationId,
        onAccepted: (redaction) => {
          // The server pass found what the device missed (AD-20).
          if (redaction !== null && redaction.summary !== null) {
            const merged = withServerSummary(
              shownNotice.current,
              redaction.summary
            );
            // A move reads the ref before the next render.
            shownNotice.current = merged;
            setNotice(merged);
            showServerText.current(redaction.text);
          }
          onTurnAccepted.current();
        },
        onMoved: (movedTo) => {
          // The new conversation's page resumes the run, so this page measures no wait.
          wait.current = null;
          onTurnMoved.current(movedTo, shownNotice.current);
        },
      }),
    [conversationId]
  );
  // Only the mount can resume before a hook call records its request.
  const attempt = useRef<Attempt>({ kind: "resume" });
  // Set by a failed run, which a retry regenerates instead of replaying.
  const failedRunId = useRef<string | null>(null);
  // Set by a send that the server could not check, until its error is cleared.
  const refusedSend = useRef(false);
  // The pauses this turn spent on reconnects, and the call that cancels the scheduled one.
  const reconnects = useRef(0);
  const scheduledReconnect = useRef<(() => void) | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  // A reconnect or a history read that ends after the page would act on a chat nobody reads.
  const mounted = useRef(true);
  const onTurnFinish = input.onTurnFinish;
  const [inputRuleSeen, markInputRuleSeen] = usePatientDataInputRuleSeen();
  // Every Question leaves through `send` or `answer`: the first one retires the
  // Input rule, and each Redaction is counted once (REQ-08).
  function notify(redaction: Redaction) {
    if (!inputRuleSeen) markInputRuleSeen();
    // A send without a Match clears the previous Notice.
    setNotice(redaction.notice);
    if (redaction.notice === null) return;
    gaEventGptPatientDataRedacted(redaction.notice.summary);
  }
  const chat = useChat<GeneralChatUIMessage>({
    id: conversationId,
    transport: assistant.transport,
    messages: input.initialMessages,
    // The user message id is the run id, so it must be a UUID.
    generateId: () => crypto.randomUUID(),
    // An answered card resumes its run; the stream's `start-step` ends that step.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onData: (part) => {
      const current = wait.current;
      if (current === null) return;
      if (part.type === "data-sources") {
        wait.current = {
          ...current,
          answeredAt:
            current.answeredAt === null
              ? performance.now()
              : current.answeredAt,
        };
        return;
      }
      // A failed pre-answer hides its text, so it shows no words.
      const preAnswer =
        part.type === "data-preAnswer"
          ? preAnswerDataSchema.parse(part.data)
          : null;
      const preAnswerShown =
        preAnswer !== null &&
        preAnswer.status !== "failed" &&
        preAnswer.text !== null;
      const draftShown = part.type === "data-draft" && part.data !== null;
      if (!preAnswerShown && !draftShown) return;
      wait.current = {
        startedAt: current.startedAt,
        preAnswer: preAnswerShown ? "shown" : current.preAnswer,
        firstWordsAt:
          current.firstWordsAt === null
            ? performance.now()
            : current.firstWordsAt,
        answeredAt: current.answeredAt,
      };
    },
    onFinish: ({ message, isAbort, isError }) => {
      endWait(message, isAbort, isError);
      if (isError) {
        // A refused send shows as an unsent message, not as a failed turn.
        if (refusedSend.current) {
          refusedSend.current = false;
          chat.clearError();
        }
        return;
      }
      endReconnects();
      onTurnFinish();
    },
    onError: (error) => {
      failedRunId.current = assistant.failedRunId();
      if (assistant.redactionRefused()) {
        // The server stored nothing: the question leaves the transcript for an
        // unsent message, and an answered card goes back to pending.
        if (attempt.current.kind === "send")
          chat.setMessages((messages) => {
            const question = messages.findLastIndex((m) => m.role === "user");
            return question === -1 ? messages : messages.slice(0, question);
          });
        // The SDK sets the error after `onError`: `onFinish` clears it.
        refusedSend.current = true;
        // Captured now: a newer send must not take its place.
        const show = onRedactionRefused.current();
        // An answered card goes back to pending first, so a Retry answers it.
        // The unsent message shows even when the history read fails.
        if (attempt.current.kind === "answer")
          void resync(toastUnreachable, false).finally(show);
        else show();
        return;
      }
      if (failedRunId.current === null && lostConnection(error) && reconnect())
        return;
      setReconnecting(false);
      // The card shows an answer the server may not hold, and the composer would
      // then ask a new question of a waiting run. The history says which holds.
      if (attempt.current.kind === "answer" && failedRunId.current === null)
        void resync(toastUnreachable, false);
    },
  });

  function toastUnreachable() {
    displayToast(t("Something went wrong. Please try again."), "error");
  }

  function cancelReconnect() {
    const cancel = scheduledReconnect.current;
    scheduledReconnect.current = null;
    if (cancel !== null) cancel();
  }

  // A finished stream or a new request from the reader gives the next break all its pauses.
  function endReconnects() {
    cancelReconnect();
    reconnects.current = 0;
    setReconnecting(false);
  }

  // False once every pause is spent.
  function reconnect(): boolean {
    const delay = RECONNECT_DELAYS_MILLISECONDS[reconnects.current];
    if (delay === undefined || !mounted.current) return false;
    reconnects.current += 1;
    setReconnecting(true);
    cancelReconnect();
    const fire = () => {
      // Offline, the request would fail at once and spend a pause for nothing.
      if (!navigator.onLine) {
        globalThis.addEventListener("online", fire, { once: true });
        scheduledReconnect.current = () => {
          globalThis.removeEventListener("online", fire);
        };
        return;
      }
      scheduledReconnect.current = null;
      void replay(() => {
        if (!reconnect()) setReconnecting(false);
      });
    };
    const timer = globalThis.setTimeout(fire, delay);
    scheduledReconnect.current = () => {
      globalThis.clearTimeout(timer);
    };
    return true;
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelReconnect();
    };
  }, []);

  // One event for each run that ends in the live view. A card wait, a lost
  // connection and a refused send end no run, so the wait goes on.
  function endWait(
    message: GeneralChatUIMessage,
    isAbort: boolean,
    isError: boolean
  ) {
    const ended = wait.current;
    if (ended === null) return;
    const outcome =
      message.metadata === undefined ? null : message.metadata.outcome;
    // A fixed reply answers no question, so the reader waited for no answer.
    if (outcome === "reply") {
      wait.current = null;
      return;
    }
    // An answer with its sources is published, even when the reader stops the wait for the follow-up question.
    const endedAs =
      ended.answeredAt !== null
        ? "published"
        : isAbort
          ? "cancelled"
          : isError
            ? failedRunId.current === null
              ? null
              : "failed"
            : outcome === "answer"
              ? "published"
              : // The stream finishes a waiting run, and aborts a run cancelled elsewhere.
                uiMessageRequest(message) === null
                ? "cancelled"
                : null;
    if (endedAs === null) return;
    wait.current = null;
    const answeredAt = endedAs === "published" ? ended.answeredAt : null;
    if (endedAs === "published" && answeredAt === null)
      throw new Error("should not happen: a published run without its sources");
    const answerSeconds =
      answeredAt === null ? null : secondsSince(ended.startedAt, answeredAt);
    gaEventGptAnswerWaitEnded({
      preAnswer: ended.preAnswer,
      outcome: endedAs,
      // Without a pre-answer or a draft, the answer brings the first words.
      firstWordsSeconds:
        ended.firstWordsAt !== null
          ? secondsSince(ended.startedAt, ended.firstWordsAt)
          : answerSeconds,
      answerSeconds,
    });
  }

  // The question bubble shows what the server stored. A card answer keeps its text until the next resync.
  showServerText.current = (text) => {
    if (attempt.current.kind !== "send") return;
    chat.setMessages((messages) => {
      const question = messages.findLastIndex((m) => m.role === "user");
      if (question === -1)
        throw new Error(
          "should not happen: an accepted send without its question"
        );
      return messages.map((message, index) =>
        index === question
          ? {
              ...message,
              parts: message.parts.map((part) =>
                part.type === "text" ? { ...part, text } : part
              ),
            }
          : message
      );
    });
  };

  // The stream broke and reopens on its own, so the turn shows no failure yet.
  // A resync that finds the run ended leaves the flag set, so the chat's status bounds it.
  const isReconnecting = reconnecting && chat.status === "error";
  const last = chat.messages.at(-1);
  useFirstTokenHaptic(
    chat.status === "streaming" &&
      last !== undefined &&
      last.role === "assistant" &&
      uiMessageHasAnswer(last)
  );

  // The submit is idempotent on the user message id, which is `requestId`.
  function send(
    requestId: string,
    redaction: Redaction,
    origin: AssistantOrigin
  ) {
    endReconnects();
    attempt.current = { kind: "send", origin };
    startWait();
    notify(redaction);
    void chat.sendMessage(
      { id: requestId, parts: [{ type: "text", text: redaction.text }] },
      { body: { origin } }
    );
  }

  function regenerate(runId: string) {
    endReconnects();
    const next = {
      kind: "regenerate",
      runId,
      requestId: crypto.randomUUID(),
    } as const;
    attempt.current = next;
    startWait();
    void chat.regenerate({
      body: { runId: next.runId, requestId: next.requestId },
    });
  }

  // A resumed run's current message is missing from the history, so the
  // stream rebuilds it whole.
  // A stop that found no stream to abort cancels the active run first, and its stream shows the stop.
  async function resync(onUnreachable: () => void, cancelActive: boolean) {
    const started = attempt.current;
    const history = await refetchApiQuery(
      "/assistant/conversations/:conversationId",
      { param: { conversationId } }
    ).then(
      (body) => body,
      () => null
    );
    if (!mounted.current) return;
    if (history === null) {
      onUnreachable();
      return;
    }
    // A send during the read owns the transcript now.
    if (attempt.current !== started) return;
    const messages = assistantUIMessages(history.messages);
    chat.setMessages(messages);
    chat.clearError();
    attempt.current = { kind: "resume" };
    const runId = activeRunId(messages);
    if (runId === null) return;
    if (cancelActive) await assistant.cancel(runId);
    await chat.resumeStream({ body: { runId } });
  }

  // The seed may be a cached read that misses the latest turns.
  useEffectOnce(() => {
    if (!input.resume) return;
    void resync(toastUnreachable, false);
  });

  async function retry() {
    const runId = failedRunId.current;
    failedRunId.current = null;
    if (runId !== null) {
      regenerate(runId);
      return;
    }
    endReconnects();
    await replay(toastUnreachable);
  }

  // Sends the broken attempt again; a reconnect calls it from a pause, so it reads the transcript when it runs.
  async function replay(onUnreachable: () => void) {
    const current = attempt.current;
    switch (current.kind) {
      case "send": {
        // The replayed stream rebuilds the partial answer from its first chunk.
        chat.setMessages((messages) => {
          const question = messages.findLastIndex((m) => m.role === "user");
          if (question === -1)
            throw new Error("should not happen: a send without its question");
          return messages.slice(0, question + 1);
        });
        void chat.sendMessage(undefined, { body: { origin: current.origin } });
        return;
      }
      case "regenerate":
        void chat.regenerate({
          body: { runId: current.runId, requestId: current.requestId },
        });
        return;
      case "answer":
      case "resume":
        await resync(onUnreachable, false);
        return;
      default:
        throw new Error(current satisfies never);
    }
  }

  // The SDK sends the answer. A refused or broken answer resyncs the history,
  // which holds the card pending or answered.
  function answer(toolCallId: string, redaction: Redaction) {
    endReconnects();
    attempt.current = { kind: "answer" };
    startWait();
    notify(redaction);
    void chat.addToolOutput({
      tool: "askUser",
      toolCallId,
      output: { answer: redaction.text },
    });
  }

  // `chat.stop` aborts only a stream it opened; the transport stops the run.
  async function stop() {
    // A card that reached the reader before the stop asks nothing anymore.
    const asked =
      last !== undefined &&
      last.role === "assistant" &&
      uiMessageRequest(last) !== null;
    // A turn that waits to reconnect holds no stream: the history shows what the stopped run wrote.
    const waiting = isReconnecting;
    endReconnects();
    await chat.stop();
    await assistant.stop();
    if (asked || waiting) await resync(toastUnreachable, waiting);
  }

  return {
    chat,
    isReconnecting,
    notice,
    dismissNotice: () => {
      setNotice(null);
    },
    send,
    regenerate,
    retry,
    stop,
    answer,
  };
}
